import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/* ────────────────────────────────────────────────────────────────────────────
   ResetPasswordPage — mini-spec #16

   Standalone page mounted at /reset-password. Rendered outside the main
   app shell (App.js routes here directly) so it works for users who
   aren't fully signed in yet — Supabase establishes a recovery-only
   session from the URL hash on its own (the email link looks like
   /reset-password#access_token=...&type=recovery&...).

   Flow:
     1. Mount → wait briefly for Supabase to consume the hash + fire
        PASSWORD_RECOVERY (the JS client does this on its own when
        detectSessionInUrl is true, which is the default).
     2. User enters new password (twice) and submits.
     3. supabase.auth.updateUser({ password }) → success → redirect to /.
   ──────────────────────────────────────────────────────────────────────── */

const STAGES = {
  RESOLVING: 'resolving',
  READY:     'ready',
  EXPIRED:   'expired',
  SUBMITTED: 'submitted',
};

export default function ResetPasswordPage() {
  const [stage, setStage]         = useState(STAGES.RESOLVING);
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState('');

  // Wait for Supabase to consume the hash and surface either a recovery
  // session or nothing (link expired / tampered).
  useEffect(() => {
    if (!supabase) {
      setStage(STAGES.EXPIRED);
      setError('Auth is not configured on this deploy.');
      return;
    }

    let canceled = false;

    // PASSWORD_RECOVERY event arrives within ms after the JS client
    // detects the hash. We also poll once after a short delay in case
    // the user landed here without a token (e.g., bookmarked the URL).
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (canceled) return;
      if (event === 'PASSWORD_RECOVERY' || (session && session.user)) {
        setStage(STAGES.READY);
      }
    });

    (async () => {
      // Give the client ~600ms to consume the hash before we declare it
      // expired. Most setups resolve in <50ms.
      await new Promise(r => setTimeout(r, 600));
      if (canceled) return;
      const { data } = await supabase.auth.getSession();
      if (data?.session?.user) {
        setStage(STAGES.READY);
      } else {
        setStage(STAGES.EXPIRED);
      }
    })();

    return () => {
      canceled = true;
      try { sub?.data?.subscription?.unsubscribe?.(); } catch {}
    };
  }, []);

  // Strip the access-token hash once we're done with it so a refresh
  // doesn't re-trigger the recovery flow.
  function clearHash() {
    try {
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    } catch {}
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      clearHash();
      setStage(STAGES.SUBMITTED);
    } catch (err) {
      setError(err?.message || 'Could not update password. Try the link again.');
    } finally {
      setBusy(false);
    }
  }

  function goHome() {
    window.location.href = '/';
  }

  return (
    <div className="rp-wrap">
      <div className="rp-card glass-panel glass-panel--lg">
        <div className="rp-brand">
          <span className="rp-brand-text">
            episode<span className="rp-brand-dot">.</span>ai
          </span>
        </div>

        {stage === STAGES.RESOLVING && (
          <>
            <h1 className="rp-title">Verifying recovery link…</h1>
            <p className="rp-sub">One moment.</p>
            <div className="rp-spinner" aria-hidden="true" />
          </>
        )}

        {stage === STAGES.READY && (
          <>
            <h1 className="rp-title">Set a new password</h1>
            <p className="rp-sub">Pick something you'll remember. Minimum 8 characters.</p>

            <form className="auth-form" onSubmit={handleSubmit}>
              <label className="auth-field">
                <span className="auth-field-label">New password</span>
                <span className="auth-input-with-icon">
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    minLength={8}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                    className="auth-input"
                  />
                  <button
                    type="button"
                    className="auth-input-eye"
                    onClick={() => setShowPw(v => !v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.79 19.79 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.79 19.79 0 0 1-3.17 4.13"/>
                        <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    )}
                  </button>
                </span>
              </label>

              <label className="auth-field">
                <span className="auth-field-label">Confirm password</span>
                <input
                  type={showPw ? 'text' : 'password'}
                  required
                  minLength={8}
                  placeholder="Re-enter password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="auth-input"
                />
              </label>

              <button
                type="submit"
                className="btn-glow-primary auth-submit"
                disabled={busy || !password || !confirm}
              >
                {busy ? 'Saving…' : 'Update password'}
              </button>
            </form>

            {error && <div className="auth-error">{error}</div>}
          </>
        )}

        {stage === STAGES.EXPIRED && (
          <>
            <h1 className="rp-title">This link has expired</h1>
            <p className="rp-sub">
              Recovery links last one hour. Request a fresh one from the sign-in screen.
            </p>
            {error && <div className="auth-error">{error}</div>}
            <button type="button" className="btn-glow-primary auth-submit" onClick={goHome}>
              Back to sign in
            </button>
          </>
        )}

        {stage === STAGES.SUBMITTED && (
          <>
            <h1 className="rp-title">Password updated</h1>
            <p className="rp-sub">You're signed in. Heading home…</p>
            <button type="button" className="btn-glow-primary auth-submit" onClick={goHome}>
              Go to Epio
            </button>
          </>
        )}
      </div>
    </div>
  );
}
